import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Tabs,
    Tab,
    AppBar,
    Toolbar,
    Container,
    TextField,
    Button,
    Box,
    Paper,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
} from '@material-ui/core';
import { useSpring, animated } from 'react-spring'
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import RateReviewIcon from '@material-ui/icons/RateReview';
import AdminImages from '../components/AdminImages';
import ImagesProvider from '../contexts/ImagesProvider';
import FeedbacksProvider from '../contexts/FeedbacksProvider';
import AdminFeedbacks from '../components/AdminFeedbacks';
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { useAuthState } from 'react-firebase-hooks/auth';
import logo from '../assets/logo-mark.png';
import { company } from '../data/company';
import { brand } from '../theme';

const useStyles = makeStyles((theme) => ({
    page: {
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
    },
    center: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(3, 2),
    },
    loginCard: {
        width: '100%',
        maxWidth: 420,
        padding: theme.spacing(5, 4),
        border: `1px solid ${brand.line}`,
        boxShadow: '0 24px 60px rgba(20,33,46,0.10)',
        [theme.breakpoints.down('xs')]: {
            padding: theme.spacing(4, 2.5),
        },
    },
    loginLogo: {
        width: 56,
        height: 56,
        borderRadius: 12,
        display: 'block',
        margin: '0 auto 16px',
    },
    loginTitle: {
        textAlign: 'center',
        marginBottom: theme.spacing(0.5),
    },
    loginSubtitle: {
        textAlign: 'center',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(4),
    },
    field: {
        marginBottom: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
        marginBottom: theme.spacing(2),
    },
    topBar: {
        backgroundColor: brand.ink,
    },
    toolbar: {
        padding: 0,
        minHeight: 64,
    },
    topLogo: {
        width: 34,
        height: 34,
        borderRadius: 8,
        backgroundColor: '#FFFFFF',
        marginRight: theme.spacing(1.5),
    },
    topTitle: {
        flexGrow: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    topActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    topButton: {
        color: '#FFFFFF',
        borderColor: 'rgba(255,255,255,0.35)',
        [theme.breakpoints.down('xs')]: {
            display: 'none',
        },
    },
    topIconButton: {
        color: '#FFFFFF',
        [theme.breakpoints.up('sm')]: {
            display: 'none',
        },
    },
    tabsBar: {
        backgroundColor: '#FFFFFF',
        borderBottom: `1px solid ${brand.line}`,
    },
    tabs: {
        '& .MuiTab-wrapper': {
            flexDirection: 'row',
            '& > *:first-child': {
                marginBottom: 0,
                marginRight: 8,
            },
        },
        '& .MuiTab-labelIcon': {
            minHeight: 56,
            paddingTop: 6,
        },
    },
    content: {
        padding: theme.spacing(4, 2, 8),
        [theme.breakpoints.up('sm')]: {
            padding: theme.spacing(5, 3, 10),
        },
    },
}));

export default function AdminPage() {
    const classes = useStyles()
    const props = useSpring({ opacity: 1, from: { opacity: 0 }, config: { duration: 500 } })
    const [value, setValue] = useState(0);
    const [email, setEmail] = useState("");
    const [pass, setPass] = useState("");
    const [loginError, setLoginError] = useState("");
    const [signingIn, setSigningIn] = useState(false);
    const auth = getAuth();
    const [user, loading] = useAuthState(auth);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleLogin = (event) => {
        event.preventDefault();
        setLoginError("");
        setSigningIn(true);
        signInWithEmailAndPassword(auth, email, pass)
            .then(() => {
                console.log("Signed in successful");
            })
            .catch((error) => {
                console.error(error);
                setLoginError("Sign-in failed. Check your email and password and try again.");
            })
            .finally(() => setSigningIn(false));
    }

    const handleSignOut = () => {
        signOut(auth).catch((error) => console.error(error));
    }

    if (loading) {
        return (
            <div className={`${classes.page} ${classes.center}`}>
                <CircularProgress color="primary" />
            </div>
        );
    }

    return (
        <animated.div style={props} className={classes.page}>
            {
                user == null ?
                    <div className={classes.center}>
                        <Paper className={classes.loginCard}>
                            <img src={logo} alt="" className={classes.loginLogo} />
                            <Typography variant="h4" component="h1" className={classes.loginTitle}>
                                Admin sign in
                            </Typography>
                            <Typography variant="body2" className={classes.loginSubtitle}>
                                Manage photos and reviews for {company.name}.
                            </Typography>
                            <form noValidate onSubmit={handleLogin}>
                                <TextField
                                    fullWidth
                                    id="admin-email"
                                    type="email"
                                    label="Email"
                                    autoComplete="username"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    className={classes.field}
                                />
                                <TextField
                                    fullWidth
                                    id="admin-password"
                                    type="password"
                                    label="Password"
                                    autoComplete="current-password"
                                    value={pass}
                                    onChange={(event) => setPass(event.target.value)}
                                    className={classes.field}
                                />
                                {loginError && (
                                    <Typography variant="body2" role="alert" className={classes.error}>
                                        {loginError}
                                    </Typography>
                                )}
                                <Button
                                    fullWidth
                                    size="large"
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={signingIn}
                                    startIcon={signingIn ? <CircularProgress size={18} color="inherit" /> : null}>
                                    Sign in
                                </Button>
                            </form>
                        </Paper>
                    </div>
                    :
                    <>
                        <AppBar position="sticky" elevation={0} className={classes.topBar}>
                            <Container maxWidth="lg">
                                <Toolbar className={classes.toolbar}>
                                    <img src={logo} alt="" className={classes.topLogo} />
                                    <Typography variant="h6" component="h1" className={classes.topTitle}>
                                        Admin dashboard
                                    </Typography>
                                    <div className={classes.topActions}>
                                        <Button
                                            href="/"
                                            variant="outlined"
                                            size="small"
                                            endIcon={<OpenInNewIcon fontSize="small" />}
                                            className={classes.topButton}>
                                            View site
                                        </Button>
                                        <Button
                                            onClick={handleSignOut}
                                            variant="contained"
                                            color="secondary"
                                            size="small"
                                            startIcon={<ExitToAppIcon fontSize="small" />}
                                            className={classes.topButton}>
                                            Sign out
                                        </Button>
                                        <Tooltip title="View site">
                                            <IconButton href="/" className={classes.topIconButton} aria-label="View site">
                                                <OpenInNewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Sign out">
                                            <IconButton onClick={handleSignOut} edge="end" className={classes.topIconButton} aria-label="Sign out">
                                                <ExitToAppIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </div>
                                </Toolbar>
                            </Container>
                        </AppBar>
                        <Box className={classes.tabsBar}>
                            <Container maxWidth="lg">
                                <Tabs
                                    value={value}
                                    onChange={handleChange}
                                    indicatorColor="secondary"
                                    textColor="primary"
                                    className={classes.tabs}
                                    aria-label="Admin sections">
                                    <Tab icon={<PhotoLibraryIcon fontSize="small" />} label="Gallery" id="admin-tab-0" aria-controls="admin-tabpanel-0" />
                                    <Tab icon={<RateReviewIcon fontSize="small" />} label="Reviews" id="admin-tab-1" aria-controls="admin-tabpanel-1" />
                                </Tabs>
                            </Container>
                        </Box>
                        <Container maxWidth="lg" className={classes.content}>
                            <div
                                role="tabpanel"
                                hidden={value !== 0}
                                id="admin-tabpanel-0"
                                aria-labelledby="admin-tab-0"
                            >
                                {value === 0 && (
                                    <ImagesProvider>
                                        <AdminImages />
                                    </ImagesProvider>
                                )}
                            </div>
                            <div
                                role="tabpanel"
                                hidden={value !== 1}
                                id="admin-tabpanel-1"
                                aria-labelledby="admin-tab-1"
                            >
                                {value === 1 && (
                                    <FeedbacksProvider>
                                        <AdminFeedbacks />
                                    </FeedbacksProvider>
                                )}
                            </div>
                        </Container>
                    </>
            }
        </animated.div>
    );
}
